// Church / organisation affiliation on the registration and account forms.
//
// Three jobs, none of which belong in combobox.js (which is generic and knows nothing about
// churches): switch between the three branches of the question, fetch and render church
// suggestions from cuf/church-search, and decide when to ask for the church's town.
//
// The town rule is the subtle one. It is only asked of members the typeahead could NOT
// identify — a church of another denomination, or a Church of England one they couldn't find.
// So it appears once they've typed a name and settled without picking a suggestion, and
// disappears the moment they do pick one, because an ACNY code already resolves to a place.
//
// Selection itself is combobox.js's job: it writes the ACNY code into the hidden input and
// announces it via combobox:select / combobox:clear, which is all this module listens for.
const affiliationRoot = document.querySelector("[data-affiliation]");

if (affiliationRoot) {
  const churchSearchEndpoint = affiliationRoot.dataset.affiliationEndpoint;
  const organisationTypeSelect = affiliationRoot.querySelector('select[name="affiliation[type]"]');
  const churchBranch = affiliationRoot.querySelector("[data-affiliation-church]");
  const townBranch = affiliationRoot.querySelector("[data-affiliation-town]");
  const otherBranch = affiliationRoot.querySelector("[data-affiliation-other]");
  const churchNameInput = affiliationRoot.querySelector("#church-name-input");
  const churchCodeInput = affiliationRoot.querySelector("#church-code-input");
  const churchListbox = affiliationRoot.querySelector("#church-listbox");
  const churchTownInput = affiliationRoot.querySelector("#church-town");

  // Mirrors LookupToolService::MIN_QUERY_LENGTH — the server enforces the same floor, so a
  // shorter term would only ever come back empty.
  const MIN_QUERY_LENGTH = 3;
  const CHURCH_TYPE = "Church";
  const MAX_SUGGESTIONS = 10;

  // Responses can land out of order; only the newest request may paint.
  let latestRequestId = 0;

  const identifiedChurch = () => churchCodeInput.value.trim() !== "";

  // Asked only of the members the typeahead couldn't place. Deliberately not evaluated on
  // every keystroke — that made the field flicker in and out as suggestions arrived.
  const refreshTownVisibility = () => {
    if (!townBranch) return;
    townBranch.hidden = churchNameInput.value.trim() === "" || identifiedChurch();
  };

  const clearSuggestions = () => {
    churchListbox.replaceChildren();
  };

  // aria-disabled, not a bare <p>: combobox.js counts rendered rows as
  // '[role="option"], [aria-disabled="true"]' when deciding whether opening the panel is
  // worthwhile, and excludes the disabled ones from keyboard nav. Without the attribute the
  // panel stays shut and the member never sees the message.
  const renderMessage = (message) => {
    const notice = document.createElement("p");
    notice.className = "m-0 px-4 py-4 text-text-primary";
    notice.setAttribute("aria-disabled", "true");
    notice.textContent = message;
    churchListbox.replaceChildren(notice);
  };

  // A church name alone is not identifiable — 10,962 of the 15,163 in the dataset carry no
  // place at all, and "All Saints" occurs 271 times — so parish, diocese and postcode travel
  // with every row. Built as DOM rather than markup: these strings are church data, not ours.
  const renderSuggestions = (churches) => {
    const suggestionList = document.createElement("ul");
    suggestionList.className = "list-none m-0 p-0 divide-y divide-borders/15";
    suggestionList.setAttribute("role", "presentation");

    churches.forEach((church) => {
      const option = document.createElement("li");
      option.id = `church-opt-${church.churchid}`;
      option.setAttribute("role", "option");
      option.className =
        "block cursor-pointer px-4 py-3 hover:bg-off-white hover:shadow-[inset_3px_0_0_#3F0F4D]";
      option.dataset.comboboxValue = church.churchid;
      option.dataset.comboboxLabel = church.churchname;

      // Focusable, or a mouse pick never registers. combobox.js closes the panel on focusout
      // unless the newly focused element is inside it, and mousedown on a row with nothing
      // focusable in it reports relatedTarget: null — so the panel hides before the click
      // lands. The Lookup Tool's rows get this for free by wrapping their content in <a href>
      // (combobox.js gives those tabindex="-1" itself); ours carry no link, so we set it here.
      option.tabIndex = -1;

      const name = document.createElement("span");
      name.className = "block body-xs font-bold";
      name.textContent = church.churchname;

      const context = [church.parish, church.diocese && `${church.diocese} diocese`, church.postcode]
        .filter(Boolean)
        .join(" · ");

      option.append(name);

      if (context) {
        const meta = document.createElement("span");
        meta.className = "block mt-px body-xs text-text-secondary";
        meta.textContent = context;
        option.append(meta);
      }

      suggestionList.append(option);
    });

    churchListbox.replaceChildren(suggestionList);
  };

  // Built via URL rather than string concatenation: actionUrl() renders as
  // `index.php?p=actions/cuf/church-search` wherever pretty URLs aren't on, so appending
  // "?q=" would produce a second "?" and a 404. searchParams appends with "&" when it needs to.
  const searchUrl = (term) => {
    const url = new URL(churchSearchEndpoint, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("limit", MAX_SUGGESTIONS);
    return url;
  };

  const searchChurches = (term) => {
    const requestId = ++latestRequestId;

    fetch(searchUrl(term), {
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then(({ results }) => {
        if (requestId !== latestRequestId) return;
        results.length
          ? renderSuggestions(results)
          : renderMessage("We can't find that church in our records. You can still type its name.");
      })
      .catch(() => {
        if (requestId !== latestRequestId) return;
        // Never block the member on our own outage — free text is a complete answer.
        renderMessage("We couldn’t load suggestions just now. You can still type your church’s name.");
      })
      .finally(() => {
        if (requestId === latestRequestId) refreshTownVisibility();
      });
  };

  let searchTimer = null;

  churchNameInput.addEventListener("input", () => {
    clearTimeout(searchTimer);
    const term = churchNameInput.value.trim();

    if (term.length < MIN_QUERY_LENGTH) {
      latestRequestId++; // abandon any response still in flight
      clearSuggestions();
      return;
    }

    searchTimer = setTimeout(() => searchChurches(term), 300);
  });

  // Settling without picking a suggestion is the signal that they couldn't be identified.
  churchNameInput.addEventListener("blur", refreshTownVisibility);

  affiliationRoot.addEventListener("combobox:select", () => {
    if (townBranch) {
      townBranch.hidden = true;
      if (churchTownInput) churchTownInput.value = "";
    }
  });

  affiliationRoot.addEventListener("combobox:clear", refreshTownVisibility);

  // Leaving a branch clears it, so a member who changes their mind can't submit answers to
  // questions they're no longer being asked. The server clears them too — this is the visible
  // half of the same rule, not the enforcement.
  const clearChurchBranch = () => {
    churchNameInput.value = "";
    churchCodeInput.value = "";
    if (churchTownInput) churchTownInput.value = "";
    if (townBranch) townBranch.hidden = true;
    clearSuggestions();
  };

  const clearOtherBranch = () => {
    otherBranch?.querySelectorAll("input").forEach((field) => {
      field.value = "";
    });
  };

  organisationTypeSelect.addEventListener("change", () => {
    const selectedType = organisationTypeSelect.value;
    const isChurch = selectedType === CHURCH_TYPE;

    churchBranch.hidden = !isChurch;
    if (otherBranch) otherBranch.hidden = isChurch || selectedType === "";

    if (isChurch) clearOtherBranch();
    else clearChurchBranch();
  });
}
